import { useState } from "react";
import { Card, CardContent, Typography, Box, Stack, Chip, Tooltip, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts/LineChart";
import { Info } from "lucide-react";
import { useTranslation } from "../../../../language";
import { useAuth } from "../../../../provider";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import TextInfoComp from "../ReuseComps/TextInfoComp";

const amhRefData = [
  { age: 20, min: 1.5, avg: 2.65, max: 3.8 },
  { age: 25, min: 1.25, avg: 2.45, max: 3.7 },
  { age: 30, min: 1.1, avg: 2.2, max: 3.3 },
  { age: 35, min: 0.65, avg: 1.8, max: 2.8 },
  { age: 37, min: 0.58, avg: 1.5, max: 2.45 },
  { age: 40, min: 0.5, avg: 1.25, max: 1.8 },
  { age: 45, min: 0.3, avg: 0.85, max: 1.25 },
  { age: 50, min: 0.2, avg: 0.6, max: 1.0 },
];


const AMHChart = ({ result }) => {
  const theme = useTheme();
  const { state } = useAuth();
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  const userAge = state.user.age;
  const userAMH = result?.value ?? null;
  if (!userAMH) return null;

  const ages = amhRefData.map((d) => d.age);
  const closestAge = amhRefData.reduce((prev, curr) =>
    Math.abs(curr.age - userAge) < Math.abs(prev.age - userAge) ? curr : prev
  );

  let ageStatus = "normal";
  if (userAMH < closestAge.min) ageStatus = "low";
  if (userAMH > closestAge.max) ageStatus = "high";

  const statusMap = {
    low:    { label: t("amh_chart_status_low"),    color: theme.palette.traffic.red },
    normal: { label: t("amh_chart_status_normal"), color: theme.palette.traffic.green },
    high:   { label: t("amh_chart_status_high"),   color: theme.palette.traffic.orange },
  };
  const { label, color } = statusMap[ageStatus];

  const cardStyles = {
    bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)",
    boxShadow: theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };

  return (
    <>
      <Card variant="outlined" sx={{ width: "100%", borderRadius: 4, position: "relative", overflow: "visible", ...cardStyles }}>
        <CardContent sx={{ pt: 3 }}>

          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {t("amh_chart_title_overline")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("amh_chart_title")}
                </Typography>
                <Tooltip title="Info" placement="right">
                  <IconButton size="small" onClick={() => setInfoOpen(true)}>
                    <Info size={18} color={theme.palette.secondary.main} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Chip
              label={label}
              sx={{ bgcolor: color, color: theme.palette.getContrastText(color), fontWeight: 700 }}
            />
          </Stack>

          <Box sx={{
            width: "100%", height: 300,
            // MUI X-Charts ships its own default axis styling that ignores most of
            // our theme.typography/palette customizations — set it explicitly so
            // the chart's font and line colors actually match the rest of the app.
            "& .MuiChartsAxis-line": { stroke: theme.palette.divider },
            "& .MuiChartsAxis-tick": { stroke: theme.palette.divider },
            "& .MuiChartsAxis-tickLabel": {
              fill: theme.palette.text.secondary,
              fontFamily: "'Helvetica Neue', sans-serif",
              fontSize: "0.75rem",
            },
            "& .MuiChartsAxis-label": {
              fill: theme.palette.text.secondary,
              fontFamily: "'Helvetica Neue', sans-serif",
              fontWeight: 500,
            },
          }}>
            <LineChart
              xAxis={[{ data: ages, label: t("amh_chart_axis_age") }]}
              yAxis={[{ min: 0, max: 6.2, label: t("amh_chart_axis_amh") }]}
              margin={{ left: 10, right: 20, top: 20, bottom: 5 }}
              slotProps={{ legend: { hidden: true } }}
              series={[
                { data: amhRefData.map((d) => d.min), stack: "range", area: true, color: "transparent", showMark: false },
                { data: amhRefData.map((d) => d.max - d.min), stack: "range", area: true, color: "url(#amhGradient)", showMark: false },
                { data: amhRefData.map((d) => d.avg), color: theme.palette.tertiary.C600, showMark: false },
                { data: amhRefData.map((d) => d.min), color: alpha(theme.palette.tertiary.C400, 0.6), showMark: false },
                { data: amhRefData.map((d) => d.max), color: alpha(theme.palette.tertiary.C400, 0.6), showMark: false },
                {
                  data: amhRefData.map((d) => d.age === closestAge.age ? userAMH : NaN),
                  color,
                  showMark: true,
                  markStyle: { r: 6, stroke: theme.palette.background.paper, strokeWidth: 2 },
                },
              ]}
            >
              <defs>
                <linearGradient id="amhGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={alpha(theme.palette.tertiary.C400, 0.45)} />
                  <stop offset="100%" stopColor={alpha(theme.palette.tertiary.C400, 0.06)} />
                </linearGradient>
              </defs>
            </LineChart>
          </Box>

        </CardContent>
      </Card>

      <CustomDialogSmall
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Information"
        content={result?.marker_short && (
          <TextInfoComp
            description={`description_${result.marker_short}`}
            lowtext={`lowtext_${result.marker_short}`}
            lowrec={`lowrec_${result.marker_short}`}
            rangetext={`rangetext_${result.marker_short}`}
            hightext={`hightext_${result.marker_short}`}
            highrec={`highrec_${result.marker_short}`}
          />
        )}
      />
    </>
  );
};

export default AMHChart;
